import React, { useState, useCallback, useEffect } from 'react';
import { kpiApi } from '../../services/api';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import {
  Home,
  FileText,
  Server,
  Tag,
  Network,
  Router,
  Cable,
  Search,
  LayoutDashboard,
  History
} from 'lucide-react';
import DashboardLayout from '../common/DashboardLayout';
import DashboardTile from '../common/DashboardTile';
import UnderConstruction from '../common/UnderConstruction';
import { useAuth } from '../../context/AuthContext';
import { PieTooltip } from '../common/ChartTooltips';

// Building colors for donut segments
const buildingColors = [
  '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1',
  '#84cc16', '#a855f7'
];

// Status colors — matches NetworkingDashboardPage
const statusColors = {
  'Active': '#22c55e',
  'Spare': '#94a3b8',
  'Pending Connection': '#f59e0b',
  'Reserved': '#3b82f6',
  'Future': '#8b5cf6',
  'Inactive': '#ef4444',
  'De-Activated': '#ef4444',
  'Unknown': '#e2e8f0'
};

const getStatusColor = (status) => statusColors[status] || statusColors['Unknown'];

const toBuildingChartData = (grouped) => {
  if (!grouped || grouped.length === 0) return [];
  return grouped.filter(g => g.count > 0).map((g, i) => ({
    name: g.key,
    value: g.count,
    fill: buildingColors[i % buildingColors.length]
  }));
};

const toStatusChartData = (grouped) => {
  if (!grouped || grouped.length === 0) return [];
  return grouped.filter(g => g.count > 0).map(g => ({
    name: g.key,
    value: g.count,
    fill: getStatusColor(g.key)
  }));
};

const BuildingDonut = ({ total, groupedData }) => {
  const chartData = toBuildingChartData(groupedData);

  return (
    <div className="network-section-content">
      <div className="network-chart-area">
        {chartData.length > 0 ? (
          <ResponsiveContainer width={130} height={130}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius={35}
                outerRadius={56}
                paddingAngle={1}
                animationDuration={600}
              >
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} stroke="none" />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
              <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" className="recharts-donut-label">
                {(total || 0).toLocaleString()}
              </text>
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="network-stat" style={{ textAlign: 'center', padding: '2rem 0' }}>
            <span className="network-stat-value" style={{ fontSize: '2.5rem' }}>
              {(total || 0).toLocaleString()}
            </span>
            <span className="network-stat-label">Total Records</span>
          </div>
        )}
      </div>

      {chartData.length > 0 && (
        <div className="network-legend">
          {chartData.map((d, i) => (
            <span key={i} className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: d.fill }} />
              <span className="legend-key">{d.name}</span>
              <span className="legend-count">{d.value.toLocaleString()}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const StatusDonut = ({ total, statusData }) => {
  const chartData = toStatusChartData(statusData);

  return (
    <div className="network-section-content">
      <div className="network-chart-area">
        {chartData.length > 0 ? (
          <ResponsiveContainer width={130} height={130}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius={35}
                outerRadius={56}
                paddingAngle={1}
                animationDuration={600}
              >
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} stroke="none" />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
              <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" className="recharts-donut-label">
                {(total || 0).toLocaleString()}
              </text>
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="network-stat" style={{ textAlign: 'center', padding: '2rem 0' }}>
            <span className="network-stat-value" style={{ fontSize: '2.5rem' }}>
              {(total || 0).toLocaleString()}
            </span>
            <span className="network-stat-label">Total Records</span>
          </div>
        )}
      </div>

      {statusData?.length > 0 && (
        <div className="network-legend">
          {statusData.map((g, i) => (
            <span key={i} className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: getStatusColor(g.key) }} />
              <span className="legend-key">{g.key}</span>
              <span className="legend-count">{g.count.toLocaleString()}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  // Not built yet: only sysadmins see these tabs, and they open to Under Construction.
  { key: 'search', label: 'Smart Search', icon: Search, sysAdminOnly: true },
  { key: 'history', label: 'Recent Changes', icon: History, sysAdminOnly: true }
];

const HomePage = ({ setCurrentPage, isAdmin }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const { isSysAdmin } = useAuth();
  const visibleTabs = TABS.filter(tab => !tab.sysAdminOnly || isSysAdmin);
  const activeTabDef = visibleTabs.find(tab => tab.key === activeTab);

  const loadKpis = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await kpiApi.getHome();
      setKpis(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch KPIs when database selection changes
  useEffect(() => {
    const handler = () => loadKpis();
    window.addEventListener('pmd-database-changed', handler);
    return () => window.removeEventListener('pmd-database-changed', handler);
  }, [loadKpis]);

  // Losing sysadmin mid-session (e.g. starting an impersonation) drops a hidden tab back to Dashboard.
  useEffect(() => {
    if (!activeTabDef) setActiveTab('dashboard');
  }, [activeTabDef]);

  return (
    <DashboardLayout
      id="home"
      title="DAWN Home"
      icon={Home}
      onRefresh={loadKpis}
      loading={loading}
      error={error}
    >
      {/* Tab bar — full width; hidden when Dashboard is the only tab */}
      {visibleTabs.length > 1 && (
      <div className="home-tab-bar" style={{ gridColumn: 'span 6' }}>
        {visibleTabs.map(tab => (
          <button
            key={tab.key}
            className={`home-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <tab.icon size={16} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>
      )}

      {/* Dashboard tab */}
      {activeTab === 'dashboard' && (
        <>
          <DashboardTile
            title="Drawings"
            icon={FileText}
            colSpan={2}
            onClick={() => setCurrentPage('drawings')}
          >
            <BuildingDonut total={kpis?.drawings} groupedData={kpis?.drawingsByBuilding} />
          </DashboardTile>

          <DashboardTile
            title="Panels"
            icon={Server}
            colSpan={2}
            onClick={() => setCurrentPage('panels')}
          >
            <BuildingDonut total={kpis?.panels} groupedData={kpis?.panelsByBuilding} />
          </DashboardTile>

          <DashboardTile
            title="Tags"
            icon={Tag}
            colSpan={2}
            onClick={() => setCurrentPage('tags')}
          >
            <BuildingDonut total={kpis?.tags} groupedData={kpis?.tagsByBuilding} />
          </DashboardTile>

          <DashboardTile
            title="Site Level Devices"
            icon={Network}
            colSpan={2}
            onClick={() => setCurrentPage('ip-address-sln')}
          >
            <StatusDonut total={kpis?.slnTotal} statusData={kpis?.slnByStatus} />
          </DashboardTile>

          <DashboardTile
            title="Device Level Devices"
            icon={Router}
            colSpan={2}
            onClick={() => setCurrentPage('ip-address-dln')}
          >
            <StatusDonut total={kpis?.dlnTotal} statusData={kpis?.dlnByStatus} />
          </DashboardTile>

          <DashboardTile
            title="Serial Devices"
            icon={Cable}
            colSpan={2}
            onClick={() => setCurrentPage('networks-serial')}
          >
            <StatusDonut total={kpis?.serialTotal} statusData={kpis?.serialByStatus} />
          </DashboardTile>
        </>
      )}

      {activeTabDef?.sysAdminOnly && (
        <div className="home-tab-content" style={{ gridColumn: 'span 6' }}>
          <UnderConstruction pageName={activeTabDef.label} />
        </div>
      )}
    </DashboardLayout>
  );
};

export default HomePage;
